import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useAuthStore } from './store/auth';
import api from './api/client';
import Layout from './components/Layout';
import ConsentBanner from './components/ConsentBanner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import ProjectsPage from './pages/ProjectsPage';
import OffersPage from './pages/OffersPage';
import InvoicesPage from './pages/InvoicesPage';
import TimePage from './pages/TimePage';
import ReportsPage from './pages/ReportsPage';
import ArticlesPage from './pages/ArticlesPage';
import AiPage from './pages/AiPage';
import CostsPage from './pages/CostsPage';
import MeasurementsPage from './pages/MeasurementsPage';
import DunningPage from './pages/DunningPage';
import SchedulePage from './pages/SchedulePage';
import AbsencesPage from './pages/AbsencesPage';
import DeliveryNotesPage from './pages/DeliveryNotesPage';
import DownloadPage from './pages/DownloadPage';
import SettingsPage from './pages/SettingsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.get('/onboarding/status').then((r) => r.data),
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <div className="flex flex-col items-center gap-3 animate-fade-in">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-glow animate-float">
            <span className="text-white text-sm font-extrabold">S</span>
          </div>
          <span className="text-sm text-slate-400 font-medium">Laden...</span>
        </div>
      </div>
    );
  }

  if (data && !data.completed) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function SentryFallback({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50">
      <div className="max-w-md text-center p-8 animate-scale-in">
        <div className="w-16 h-16 rounded-2xl bg-error-50 flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Etwas ist schiefgelaufen</h1>
        <p className="text-sm text-slate-400 mb-5">
          Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.
        </p>
        <pre className="text-xs text-error-600 bg-error-50 border border-error-100 p-4 rounded-xl mb-6 overflow-auto text-left font-mono">
          {message}
        </pre>
        <button onClick={() => window.location.reload()} className="sp-btn-primary">
          Seite neu laden
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);

  // Sync Sentry user context with auth state
  useEffect(() => {
    if (user && tenant) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
      });
      Sentry.setTag('tenantId', tenant.id);
      Sentry.setTag('tenantPlan', tenant.plan);
    } else {
      Sentry.setUser(null);
    }
  }, [user, tenant]);

  return (
    <Sentry.ErrorBoundary fallback={({ error }) => <SentryFallback error={error} />}>
      <ConsentBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <Layout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/offers" element={<OffersPage />} />
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/time" element={<TimePage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/measurements" element={<MeasurementsPage />} />
                    <Route path="/articles" element={<ArticlesPage />} />
                    <Route path="/costs" element={<CostsPage />} />
                    <Route path="/dunning" element={<DunningPage />} />
                    <Route path="/schedule" element={<SchedulePage />} />
                    <Route path="/absences" element={<AbsencesPage />} />
                    <Route path="/delivery-notes" element={<DeliveryNotesPage />} />
                    <Route path="/ai" element={<AiPage />} />
                    <Route path="/download" element={<DownloadPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </Layout>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Sentry.ErrorBoundary>
  );
}
